/**
 * Global per-page ad budget.
 *
 * Rule: every page can display AT MOST 5 ad units, counted across:
 *   - <AdSlot> React components (banner / video / roadblock / text)
 *   - Inline `.ad-slot` blocks embedded in blog markdown via the editor's "+ Ad" button
 *   - Awin fallback creative served in empty AdSlots
 *
 * Callers:
 *   • `consume()` — atomically request a slot; returns true if under the cap.
 *   • `reset()`   — invoked on every route change (App.js) so a new page starts fresh.
 *   • `remaining()` — for renderers that need to know how many they can still show
 *                     (e.g. ArticleContent counts inline ads in markdown before render).
 *   • MAX — the global cap. Change here if the rule ever loosens.
 */

export const MAX = 5;

let used = 0;
const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(used); } catch { /* swallow, a bad listener must not break rendering */ }
  }
}

export function consume(n = 1) {
  const room = Math.max(0, MAX - used);
  const take = Math.min(n, room);
  used += take;
  if (take > 0) notify();
  return take === n;   // true if the caller got the full requested count
}

export function remaining() {
  return Math.max(0, MAX - used);
}

export function reset() {
  if (used === 0) return;
  used = 0;
  notify();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
